import { useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import styles from './PieChart.module.scss';
import { useTranslation } from 'react-i18next';
import { LoaderSmall } from '../LoaderSmall/LoaderSmall';
import {
  buildDonutOption,
  getDonutClickIndex,
  DonutDatum,
  DonutLegendPosition,
} from '../../utils/echartsDonut';

export type PieChartDatum = DonutDatum;

interface LegendConfig {
  position?: DonutLegendPosition;
  textStyle?: {
    fontSize?: number;
    color?: string;
  };
}

interface PieChartProps {
  isClickable?: boolean;
  colors?: string[];
  chartData: PieChartDatum[] | null;
  legendConfig?: LegendConfig;
  /** Receives the index of the clicked slice within `chartData`. */
  onSelect?: (selected: number | null) => void;
  chartBadge?: string;
  seriesName?: string;
}

const CHART_HEIGHT = 350;

export const PieChart: React.FC<PieChartProps> = ({
  chartData = null,
  chartBadge = '',
  isClickable = true,
  onSelect = () => {},
  seriesName,
  colors = [
    '#0043CE',
    '#0F62FE',
    '#4589FF',
    '#78A9FF',
    '#A6C8FF',
    '#D0E2FF',
    '#EDF5FF',
    '#002D9C',
  ],
  legendConfig = {
    position: 'labeled',
    textStyle: { fontSize: 14 },
  },
}) => {
  const { t } = useTranslation();
  const chartRef = useRef<ReactECharts>(null);

  const legendPosition = legendConfig.position ?? 'labeled';
  const labelFontSize = legendConfig.textStyle?.fontSize ?? 14;
  const labelColor = legendConfig.textStyle?.color;

  const option = useMemo(
    () =>
      buildDonutOption({
        data: chartData ?? [],
        colors,
        legendPosition,
        labelFontSize,
        labelColor,
        isClickable,
        seriesName,
        getChartWidth: () => chartRef.current?.getEchartsInstance()?.getWidth() ?? 0,
      }),
    [chartData, colors, legendPosition, labelFontSize, labelColor, isClickable, seriesName],
  );

  const onEvents = useMemo(() => {
    if (!isClickable) return undefined;
    return {
      click: (params: any) => {
        const index = getDonutClickIndex(params, chartRef.current?.getEchartsInstance());
        if (index !== null) onSelect(index);
      },
    };
  }, [isClickable, onSelect]);

  if (chartData === null) {
    return (
      <div className={styles.container}>
        <div className={styles.loaderWrapper}>
          <LoaderSmall />
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.noData}>{t('There is no data to display')}</div>
      </div>
    );
  }

  return (
    <div className={styles.container} translate="no">
      {chartBadge.length > 0 ? (
        <div className={styles.chartBadge}>{chartBadge}</div>
      ) : null}
      <ReactECharts
        ref={chartRef}
        option={option}
        notMerge
        style={{ width: '100%', height: `${CHART_HEIGHT}px` }}
        opts={{ renderer: 'svg' }}
        onEvents={onEvents}
      />
    </div>
  );
};
